import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {Html5Qrcode, Html5QrcodeScannerState} from 'html5-qrcode';
import {QrDimensions} from 'html5-qrcode/core';
import {BehaviorSubject, distinctUntilChanged, map, Observable, Subject, Subscription} from 'rxjs';
import {BooleanCameraCapability, CameraDevice} from 'html5-qrcode/camera/core';
import {Html5QrcodeCameraScanConfig} from 'html5-qrcode/html5-qrcode';

// FPS des Scanners
const MITS_SCANNER_FPS = 15;


/**
 * Globaler Service zur Bereitstellung eines Scanners zum Scannen von QR-Codes
 * - Stellt sicher, dass nur ein Scanner gleichzeitig aktiv ist
 * - Stellt das Ergebnis des Scans als Observable bereit (scanResult$)
 * - Bietet Methoden zum Starten und Stoppen des Scanners (startScanner, stopScanner)
 */
@Injectable({providedIn: 'root'})
export class MitsScannerService implements OnDestroy {
  // Instanz des Scanners (Instanz von html5-qrcode)
  private html5QrCode?: Html5Qrcode | undefined;
  // Ergebnis des Scans als BehaviorSubject
  private scanResult!: Subject<string>;
  // ResizeObserver zur Beobachtung der Größenänderung des Scanner-Videostream-Containers
  private resizeObserver?: ResizeObserver;
  // Referenz auf das Element, in dem der Videostream des Scanners angezeigt wird
  private videoStreamContainerElement!: HTMLElement;
  // Subject zur Erfassung von Resize-Ereignissen
  private resizeSubject: Subject<ResizeObserverEntry> = new Subject<ResizeObserverEntry>();
  // Subscription zur Verwaltung der Resize-Events
  private resizeSubscription?: Subscription;
  // BehaviorSubject zur Anzeige der Scanbereitschaft des Scanners
  private isReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isReady$: Observable<boolean> = this.isReady.asObservable();
  // BehaviorSubject zur Anzeige, ob die Taschenlampenfunktion unterstützt wird
  private torchSupportedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public torchSupported$: Observable<boolean> = this.torchSupportedSubject.asObservable();
  // Zustand der Taschenlampe
  private isTorchOn = false;
  // ID des Elements, in dem der Videostream des Scanners angezeigt wird
  private videoStreamContainerId: string | null = null;
  // Flag zur Überprüfung, ob der Scanner bereits einmalig initialisiert wurde
  private isInitialized = false;

  constructor(
    // Referenz auf das Dokument (DOM)
    @Inject(DOCUMENT)
    private document: Document
  ) {
    this.initResizeSubscription();
    this.scanResult = new Subject<string>();
  }

  ///////////////////////////// LIFECYCLE-HOOKS /////////////////////////////

  /**
   * Lifecycle-Hook: Wird aufgerufen, wenn die Instanz zerstört wird
   */
  async ngOnDestroy(): Promise<void> {
    // ResizeObserver stoppen
    this.resizeObserver?.disconnect();
    // Subscription für Resize-Ereignisse beenden
    this.resizeSubscription?.unsubscribe();
    // ScanResult-Subject beenden
    this.scanResult.complete();
    // Scanner stoppen
    await this.stopHtml5Qrcode();
    // HTML5QrCode-Instanz "lösen"
    this.html5QrCode = undefined;
  }

  ///////////////////////////// ÖFFENTLICHE METHODEN /////////////////////////////

  setVideoStreamContainerId(videoStreamContainerID: string): void {
    this.videoStreamContainerId = videoStreamContainerID;
    const videoStreamContainerElement: HTMLElement | null = this.getVideoStreamContainerElement(this.videoStreamContainerId);
    if(!videoStreamContainerElement) return;
    this.videoStreamContainerElement = videoStreamContainerElement;
    this.initResizeObserver();
  }


  /**
   * Initialisiert den Scanner (Führt zur Abfrage der Berechtigung)
   */
  private async init(): Promise<boolean> {
    if(this.isInitialized) return true;
    if(!await this.initHTML5QRCode()) return false;
    this.isInitialized = true;
    return true;
  }

  /**
   * Methode zum Umschalten der Taschenlampe
   */
  public toggleTorch(): void {
    if (this.html5QrCode) {
      const torch: BooleanCameraCapability = this.html5QrCode.getRunningTrackCameraCapabilities()?.torchFeature();
      if (torch?.isSupported()) {
        const newTorchState = !this.isTorchOn;
        torch.apply(newTorchState)
          .then(() => {
            this.isTorchOn = newTorchState;
          })
          .catch(error => {
            console.error('[MitsScannerService][toggleTorch()] Fehler beim Umschalten der Taschenlampe', error);
          });
      }
    }
  }

  /**
   * Gibt das Ergebnis des Scans als Observable zurück
   * @returns Observable<string | null> - Ergebnis des Scans
   */
  get scanResult$(): Observable<string | null> {
    return this.scanResult.asObservable();
  }

  ///////////////////////////// PRIVATE METHODEN /////////////////////////////

  /**
   * Gibt das Element zurück, in dem der Videostream des Scanners angezeigt wird
   * @param videoStreamContainerID
   * @private
   */
  private getVideoStreamContainerElement(videoStreamContainerID: string): HTMLElement | null {
    try {
      return this.document.getElementById(videoStreamContainerID);
    } catch (error) {
      console.error(
          `[MitsScannerService][getVideoStreamContainerElement()]: Fehler beim Abrufen des Video-Stream-Container-Elements`,
          videoStreamContainerID,
          error);
      return null;
    }
  }

  /**
   * Initialisiert den ResizeObserver für das Scanner-Viewfinder-Element
   * - Der ResizeObserver wird verwendet, um den Scanner neu zu starten, wenn sich die Größe des Viewfinders ändert
   * @private
   */
  private initResizeObserver(): boolean {
    try {
      this.resizeObserver = new ResizeObserver((r: ResizeObserverEntry[]): void => {
        this.resizeSubject.next(r[0]);
      });
      this.resizeObserver.observe(this.videoStreamContainerElement);
      return true;
    } catch (error) {
      console.error(`[MitsScannerService][initResizeObserver()]: Initialisierung des ResizeObserver fehlgeschlagen`, error);
      return false;
    }
  }


  /**
   * Initialisiert das HTML5QRCode-Objekt für den Scanner
   * @private
   */
  private async initHTML5QRCode(): Promise<boolean> {
    try {
      const devices: CameraDevice[] = await Html5Qrcode.getCameras();
      if (devices && devices.length) {
        this.html5QrCode = new Html5Qrcode(this.videoStreamContainerElement.id);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[MitsScannerService][initScanner()]: Initialisierung des Scanners fehlgeschlagen', error);
      return false;
    }
  }

  /**
   * Startet den Scanner
   * - Das Ergebnis des Scans wird über das Observable scanResult$ bereitgestellt
   * @returns Promise<boolean> - true, wenn der Scanner erfolgreich gestartet wurde, sonst false
   */
  private async startHtml5Qrcode(): Promise<boolean> {
    try {
      await this.init();
      if (this.html5QrCode) {
        this.isReady.next(false);
        await this.html5QrCode.start(
          this.generateMediaTrackConstraints(), // Konfiguration der Kamera
          this.generateConfig(), // Konfiguration des Scanners
          (decodedText: string) => {
            this.scanResult.next(decodedText)
          }, // Callback bei erfolgreichem Scan
          undefined // Callback bei fehlerhaftem Scan
        );
        // Prüfen, ob die Taschenlampenfunktion unterstützt wird
        if (this.html5QrCode.getRunningTrackCameraCapabilities()?.torchFeature()?.isSupported()) {
          this.torchSupportedSubject.next(true);
        }
        this.applyStylesToVideoElement();
        this.isReady.next(true);
        return true;
      } else {
        console.warn('[MitsScannerService][startScanner()]: HTML5QrCode-Instanz nicht initialisiert');
        return false;
      }
    } catch (error) {
      console.error('[MitsScannerService][startScanner()]: Starten des Scanners fehlgeschlagen', error);
      return false;
    }
  }

  /**
   * Stoppt den Scanner
   * @private
   */
  private async stopHtml5Qrcode(): Promise<void> {
    if (this.html5QrCode && this.html5QrCode.getState() !== Html5QrcodeScannerState.NOT_STARTED) {
      return await this.html5QrCode.stop();
    }
  }


  /**
   * Generiert die Konfiguration für die Kamera
   * @private
   */
  private generateMediaTrackConstraints(): MediaTrackConstraints {
    return {
      facingMode: 'environment',
    };
  }

  /**
   * Generiert die Konfiguration des Scanners
   * @returns Html5QrcodeCameraScanConfig - Konfiguration des Scanners
   */
  private generateConfig(): Html5QrcodeCameraScanConfig {
    const element: HTMLElement = this.videoStreamContainerElement;
    const isMobile: boolean = window.innerWidth < 768 && window.matchMedia('(max-width: 768px)').matches;
    return {
      fps: MITS_SCANNER_FPS,
      qrbox: this.generateQrBox(
        // Bei Mobilgeräten das Verhältnis umkehren (siehe https://github.com/mebjas/html5-qrcode/issues/387#issuecomment-1047386145)
        element.clientWidth,
        isMobile? element.clientWidth : element.clientHeight
      ),
      videoConstraints: {
        facingMode: 'environment',
        // @ts-expect-error torch ist im html5-qrcode-Typ nicht deklariert
        torch: true,
        // Bei Mobilgeräten das Verhältnis umkehren (siehe https://github.com/mebjas/html5-qrcode/issues/387#issuecomment-1047386145)
        aspectRatio: isMobile
          ? element.clientHeight / element.clientWidth
          : element.clientWidth / element.clientHeight
      },
      rememberLastUsedCamera: true,
    };
  }

  private applyStylesToVideoElement(): void {
    const videoElement: HTMLVideoElement | null = this.videoStreamContainerElement.firstElementChild as HTMLVideoElement | null;
    if(videoElement) {
      videoElement.style.width = '100%';
      videoElement.style.objectFit = 'contain';
    }
  }

  /**
   * Hilfsmethode zur Generierung der Abmessung des Scan-Bereichs auf Basis der Abmessungen des Viewfinders
   * @param viewfinderWidth - Breite des Viewfinders
   * @param viewfinderHeight - Höhe des Viewfinders
   * @returns QrDimensions - Abmessungen des Scan-Bereichs
   */
  private generateQrBox(viewfinderWidth: number, viewfinderHeight: number): QrDimensions {
    // Minimale Kantenlänge des Viewfinders
    const minEdgeSize: number = Math.min(viewfinderWidth, viewfinderHeight);
    return {
      width: Math.floor(minEdgeSize * 0.8), // 90 % der minimalen Kantenlänge
      height: Math.floor(minEdgeSize * 0.8)
    };
  };

  /**
   * Initialisiert die Subscription für Resize-Ereignisse, um auf das Öffnen des Modals zu reagieren
   * @private
   */
  private initResizeSubscription(): void {
    this.resizeSubscription = this.resizeSubject
      .pipe(
        // Extrahieren von Breite und Höhe des Modals aus den Resize-Events
        map((entry: ResizeObserverEntry): {width: number, height: number} => {
          const { width, height } = entry?.contentRect?? { width: 0, height: 0 };
          return { width, height };
        }),
        // Nur fortfahren, wenn sich Breite oder Höhe geändert haben
        distinctUntilChanged((prev: {width: number, height: number}, curr: {width: number, height: number}): boolean =>
          prev.width === curr.width && prev.height === curr.height)
      )
      .subscribe(async ({ width, height }) => {
        if(this.videoStreamContainerId === null) return;
        if (width === 0 || height === 0) {
          await this.stopHtml5Qrcode();
        } else {
          // Zur Sicherheit den Scanner stoppen und neu starten
          await this.stopHtml5Qrcode();
          await this.startHtml5Qrcode();
        }
      });
  }
}
